import type { DeviceConfigResponse } from "@shared/admin";
import type { AdminAuthOptions, AdminAuthOptionsRequest, AuthSession, BootstrapStoreOption, DeviceBootstrapAuthorization, DeviceBootstrapAuthorizationRequest, DeviceBootstrapRequest, LoginRequest, OperatorDeviceContext, StoreSwitchRequest, UnifiedAdminAuthOptions, UnifiedAdminSession } from "@shared/identity";

export type DeviceBootstrapResult = {
  context: OperatorDeviceContext;
  snapshot: import("@shared/admin").DeviceConfigSnapshot;
  created: boolean;
};

export class AuthClient {
  constructor(private readonly fetcher: typeof fetch = fetch) {}

  async session(): Promise<AuthSession | null> {
    const response = await this.fetcher.call(globalThis, "/api/auth/session", { credentials: "same-origin" });
    if (response.status === 401) return null;
    if (!response.ok) throw new Error(`Session request failed (${response.status})`);
    return ((await response.json()) as { session: AuthSession }).session;
  }

  async login(input: LoginRequest): Promise<AuthSession> {
    const response = await this.fetcher.call(globalThis, "/api/auth/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Login failed (${response.status})`);
    }
    return ((await response.json()) as { session: AuthSession }).session;
  }

  async authorizeAdmin(input: AdminAuthOptionsRequest): Promise<AdminAuthOptions> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/options", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Authentication failed (${response.status})`);
    }
    return (await response.json()) as AdminAuthOptions;
  }

  async stores(): Promise<BootstrapStoreOption[]> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/stores", { credentials: "same-origin" });
    if (!response.ok) throw new Error(`Store list failed (${response.status})`);
    return ((await response.json()) as AdminAuthOptions).stores;
  }

  async switchStore(input: StoreSwitchRequest): Promise<AuthSession> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/switch-store", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Store switch failed (${response.status})`);
    }
    return ((await response.json()) as { session: AuthSession }).session;
  }

  async logout(): Promise<void> {
    const response = await this.fetcher.call(globalThis, "/api/auth/logout", { method: "POST", credentials: "same-origin" });
    if (!response.ok) throw new Error(`Logout failed (${response.status})`);
  }

  async unifiedOptions(input: AdminAuthOptionsRequest): Promise<UnifiedAdminAuthOptions> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/options", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Authentication failed (${response.status})`);
    }
    return (await response.json()) as UnifiedAdminAuthOptions;
  }

  async unifiedLogin(input: AdminAuthOptionsRequest & { storeId?: string }): Promise<UnifiedAdminSession> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Login failed (${response.status})`);
    }
    return (await response.json()) as UnifiedAdminSession;
  }

  async unifiedSession(): Promise<UnifiedAdminSession | null> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/session", { credentials: "same-origin" });
    if (response.status === 401) return null;
    if (!response.ok) throw new Error(`Session request failed (${response.status})`);
    return (await response.json()) as UnifiedAdminSession;
  }

  async unifiedLogout(): Promise<void> {
    const response = await this.fetcher.call(globalThis, "/api/admin/auth/logout", { method: "POST", credentials: "same-origin" });
    if (!response.ok) throw new Error(`Logout failed (${response.status})`);
  }

  async bootstrapDevice(input: DeviceBootstrapRequest): Promise<DeviceBootstrapResult> {
    const response = await this.fetcher.call(globalThis, "/api/device/bootstrap", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; stores?: unknown };
      const error = new Error(body.error ?? `Device bootstrap failed (${response.status})`) as Error & { status?: number; stores?: unknown };
      error.status = response.status;
      error.stores = body.stores;
      throw error;
    }
    return (await response.json()) as DeviceBootstrapResult;
  }

  async authorizeBootstrap(input: DeviceBootstrapAuthorizationRequest): Promise<DeviceBootstrapAuthorization> {
    const response = await this.fetcher.call(globalThis, "/api/device/bootstrap/options", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Owner authentication failed (${response.status})`);
    }
    return (await response.json()) as DeviceBootstrapAuthorization;
  }

  async deviceConfig(after = 0): Promise<DeviceConfigResponse> {
    const response = await this.fetcher.call(globalThis, `/api/device/config?after=${encodeURIComponent(String(after))}`, { credentials: "same-origin" });
    if (!response.ok) throw new Error(`Device Config request failed (${response.status})`);
    return (await response.json()) as DeviceConfigResponse;
  }
}
